import { createApp } from 'vue';
import { createStore } from 'vuex';
import { createRouter, createWebHashHistory } from 'vue-router';
import { createHead } from '@unhead/vue/client';
import App from './App.vue';
import { routes, setupRouterGuards } from './router/router';
import VueToast from 'vue-toast-notification';
import ActionCableVue from 'actioncable-vue';
import { vue3Debounce } from 'vue-debounce';
import { Field, Form, ErrorMessage, defineRule, configure } from 'vee-validate';
import { required, email, min, max } from '@vee-validate/rules';
import { localize } from '@vee-validate/i18n';
import en from '@vee-validate/i18n/dist/locale/en.json';
import JsonViewer from 'vue-json-viewer';
import { Notifier } from '@airbrake/browser';
import vueCountryRegionSelect from 'vue3-country-region-select';
import VueTippy from "vue-tippy";
import "tippy.js/themes/light.css";
import "tippy.js/themes/light-border.css";
import "tippy.js/themes/translucent.css";
import "tabulator-tables/dist/css/tabulator.min.css";
import 'vue-toast-notification/dist/theme-bootstrap.css';
import { createVfm } from 'vue-final-modal';
import 'vue-final-modal/style.css';
import { ModalPlugin } from './utils/useModal';
import { infiniteScrollDirective } from './utils/useInfiniteScroll';
import Modal from './components/Modal.vue';

// Configure vee-validate rules
defineRule('required', required);
defineRule('email', email);
defineRule('min', min);
defineRule('max', max);

configure({
  generateMessage: localize('en', en),
});

const store = createStore({
  state: {
    cloningStatus: false,
    cloningElm: null,
    cloneProgress: 0,
    importElmStatus: false,
    importingElm: null,
    elmImportProgress: 0,
    importWorldstatus: false,
    importingWorld: null,
    worldImportProgress: 0,
    exportElmStatus: false,
    exportingElm: null,
    elmExportProgress: 0,
    llmData: {},
    newLo: false
  },
  mutations: {
    SET_ELM_CLONING(state, elm){
      state.cloningElm = elm;
    },
    SET_CLONING_STATUS(state, status){
      state.cloningStatus = status;
    },
    SET_CLONING_PROGRESS(state, progress){
      state.cloneProgress = progress;
    },
    SET_ELM_IMPORT(state, elm){
      state.importingElm = elm;
    },
    SET_ELM_IMPORT_STATUS(state, status){
      state.importElmStatus = status;
    },
    SET_ELM_IMPORT_PROGRESS(state, progress){
      state.elmImportProgress = progress;
    },
    SET_WORLD_IMPORT(state, elm){
      state.importingWorld = elm;
    },
    SET_WORLD_IMPORT_STATUS(state, status){
      state.importWorldstatus = status;
    },
    SET_WORLD_IMPORT_PROGRESS(state, progress){
      state.worldImportProgress  = progress;
    },
    SET_ELM_EXPORT(state, elm){
      state.exportingElm = elm;
    },
    SET_ELM_EXPORT_STATUS(state, status){
      state.importWorldstatus = status;
    },
    SET_ELM_EXPORT_PROGRESS(state, progress){
      state.elmExportProgress  = progress;
    },
    SET_LLM_DATA(state, data) {
      state.llmData = data;
    },
    SET_NEW_LO(state, data) {
      state.newLo = data
    }
  },
  actions: {
    startClone({commit}, elm){
      commit('SET_CLONING_STATUS', true)
      commit('SET_ELM_CLONING', elm)
    },
    endClone({commit}){
      commit('SET_CLONING_STATUS', false)
      commit('SET_ELM_CLONING', null)
      commit('SET_CLONING_PROGRESS', 0)
    },
    updateCloneProgress({commit}, progress){
      commit('SET_CLONING_PROGRESS', progress)
    },
    startElmImport({commit}, elm){
      commit('SET_ELM_IMPORT_STATUS', true)
      commit('SET_ELM_IMPORT', elm)
    },
    endElmImport({commit}){
      commit('SET_ELM_IMPORT_STATUS', false)
      commit('SET_ELM_IMPORT', null)
      commit('SET_ELM_IMPORT_PROGRESS', 0)
    },
    updateElmImportProgress({commit}, progress){
      commit('SET_ELM_IMPORT_PROGRESS', progress)
    },
    startWorldImport({commit}){
      commit('SET_WORLD_IMPORT_STATUS', true)
      // commit('SET_WORLD_IMPORT', elm)
    },
    endWorldImport({commit}){
      commit('SET_WORLD_IMPORT_STATUS', false)
      commit('SET_WORLD_IMPORT', null)
      commit('SET_WORLD_IMPORT_PROGRESS', 0)
    },
    updateWorldlmImportProgress({commit}, progress){
      commit('SET_WORLD_IMPORT_PROGRESS', progress)
    },
    updateLlmData({commit}, data) {
      commit('SET_LLM_DATA', data)
    },
    updateNewLo({commit}, data) {
      commit('SET_NEW_LO', data)
    }
  }
});

// Create router instance
const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active"
});

// Setup router guards
setupRouterGuards(router);

// Create head instance for meta tags
const head = createHead();

// Create vue-final-modal instance
const vfm = createVfm();

// Create Vue app
const app = createApp(App);

// Use plugins
app.use(router);
app.use(store);
app.use(head);
app.use(vfm);
app.use(ModalPlugin);
app.use(vueCountryRegionSelect);
app.use(JsonViewer);
app.directive('debounce', vue3Debounce({
  defaultTime: '1200ms'
}));
app.use(VueToast);
app.use(VueTippy, {
  directive: 'tippy',
  component: 'tippy'
});

// Configure ActionCable
app.use(ActionCableVue, {
  debug: true,
  debugLevel: 'error',
  connectionUrl: process.env.VUE_APP_ACTION_CABLE_URL,
  connectImmediately: true
});

// Register global directives
app.directive('infinite-scroll', infiniteScrollDirective);

// Register global components
app.component('modal', Modal);
app.component('VeeField', Field);
app.component('VeeForm', Form);
app.component('VeeErrorMessage', ErrorMessage);

// Global mixin
app.mixin({
  created() {
    //console.log('[created] ' + this.$options.name)
  },
});

// Airbrake error handler
if (process.env.NODE_ENV === 'production') {
  var airbrake = new Notifier({
    environment: 'production',
    projectId: process.env.VUE_APP_AIRBRAKE_PRODUCT_ID,
    projectKey: process.env.VUE_APP_AIRBRAKE_PRODUCT_KEY
  });
  app.config.errorHandler = function (err, vm, info) {
    airbrake.notify({
      error: err,
      params: {info: info}
    });
  };
}

// Mount app
app.mount('#app');